import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import './PrivacyPolicyPage.css';

interface PrivacyPolicyPageState {
  // Simple state matching reference project
}

interface PrivacyPolicyPageProps extends WithTranslation {
  navigate?: (path: string) => void;
}

// Simple interface for privacy policy content
interface PrivacyPolicyContent {
  htmlContent: string;
}

class PrivacyPolicyPage extends Component<PrivacyPolicyPageProps, PrivacyPolicyPageState> {
  constructor(props: PrivacyPolicyPageProps) {
    super(props);
    this.state = {};
  }

  // Phase 2: Navigation method - Navigate back to settings
  goBack = () => {
    const { navigate } = this.props;
    if (navigate) {
      // Navigate back to settings page (reference project behavior)
      navigate('/settings');
    }
  };

  // Privacy Policy Content - Single HTML variable for easy management
  // EASY MANAGEMENT:
  // 1. Edit HTML directly in this string for content updates
  // 2. Copy/paste from HTML editor or Word (save as HTML)
  // 3. Replace entire string with CMS/database content
  // 4. Use template literals for dynamic values: ${variable}
  //
  // FUTURE DYNAMIC OPTIONS:
  // 1. Redux: this.props.privacyPolicyContent?.htmlContent
  // 2. API: await this.props.getPrivacyPolicyContent()
  // 3. CMS: fetch from admin panel
  // 4. File: import from separate .html file
  getPrivacyPolicyContent = (): PrivacyPolicyContent => {
    // Future: return { htmlContent: this.props.privacyPolicyContent?.htmlContent || this.getDefaultContent() };
    return {
      htmlContent: `
        <p>
          This Privacy Policy explains how The Child Development Centre (CDC) collects, uses, shares and protects the users of our mobile application, website and CDC content, such as text, graphics, videos, images, information, and other materials (the "Service"). If you choose to use our Service, then you agree to the collection and use of information in relation to this policy. The information relating directly or indirectly to a living individual from which it is practical to ascertain the identity of the said individual (Personal Information) that we collect is used for providing and improving the Service. We will not use or share your Personal Information with anyone except as described in this Privacy Policy.
        </p>

        <h3>Using CDC's Mobile Applications (the "App")</h3>
        <p>
          The App is provided by CDC at no cost and is intended for use "AS-IS". Certain non-Personal Information may be gathered from your use of the App. Such information may be provided to third-party analytical service Google Analytics in order to track and monitor use of the App. This may include information such as the number of times the App has been accessed, users' engagement with specific areas of the App, and users' activities within the App. Such third party analytical services shall comply with all applicable laws, policies and regulations relating to the collection of information from users. For more information on Google's privacy practices, please visit Google Analytics policies.
        </p>
        <p>
          If you choose to use our App, then you acknowledge and agree to the terms of this Privacy Policy. Do not use the App or give us any of your information if you do not agree to the terms of this Privacy Policy. However, not providing information may affect certain Services.
        </p>

        <h3>Collection and Use of Personal Information</h3>
        <p>
          While using our Service, we may collect and process data about you including:
        </p>
        <p>• your name, mobile number and email;</p>
        <p>• any other information you provide for the purpose of making online donations; and</p>
        <p>• cookies information as mentioned below.</p>
        <p>
          CDC shall not disclose, share, sell or transfer any Personal Information to third parties without your express permission or unless required by law.
        </p>

        <h3>Cookies</h3>
        <p>
          Cookies are files with a small amount of data that are commonly used as anonymous, unique identifiers. These are sent to your browser from the websites you visit and are stored on your device's internal memory.
        </p>
        <p>
          Our Service may use third party code and libraries that use "cookies" to collect information to improve their services. You have the option to either accept or refuse these cookies and know when a cookie is being sent to your device. If you choose to refuse our cookies, some portions of this Service may be limited.
        </p>

        <h3>Service Providers</h3>
        <p>We may employ third-party companies and individuals due to the following reasons:</p>
        <p>• To facilitate our Service;</p>
        <p>• To provide the Service on our behalf;</p>
        <p>• To perform Service-related services; or</p>
        <p>• To assist us in analyzing how our Service is used.</p>
        <p>
          We want to inform users of this Service that these third parties have access to your Personal Information. The reason is to perform the tasks assigned to them on our behalf. However, they are obligated not to disclose or use the information for any other purpose.
        </p>

        <h3>Security</h3>
        <p>
          We value your trust in providing us with your Personal Information, thus we are striving to use all practicable steps of protecting it. However, remember that no method of transmission over the internet, or method of electronic storage is 100% secure and reliable, and we cannot guarantee its absolute security.
        </p>

        <h3>Your Rights</h3>
        <p>
          Under the Hong Kong Personal Data (Privacy) Ordinance, you have the right of access and correction with respect to personal data. Request for personal data access and correction should be made to CDC in writing. In addition, if you do not wish to receive any communication relating to our Service, you may contact us at info@cdchk.org any time. Please mark the request to the attention of the Community Development Team.
        </p>

        <h3>Links to Other Sites</h3>
        <p>
          This Service may contain links to other sites. If you click on a third-party link, you will be directed to that site. Note that these external sites are not operated by us. Therefore, we strongly advise you to review the Privacy Policy of these websites. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
        </p>

        <h3>Changes to This Privacy Policy</h3>
        <p>
          We may update our Privacy Policy from time to time. Thus, you are advised to review this page periodically for any changes. We will notify you of any changes by posting the new Privacy Policy on this page. These changes are effective immediately after posting.
        </p>

        <h3>Contact Us</h3>
        <p style="padding-bottom: 50px;">
          If you have any questions or concerns about how your information is being used. Please feel free to contact us at info@cdchk.org
        </p>
      `
    };
  };

  render() {
    const { t } = this.props;
    const privacyContent = this.getPrivacyPolicyContent();

    return (
      <div className="privacy-policy-page">
        <div className="privacy-policy-container">
          {/* Header - matching reference project structure */}
          <div className="page-header">
            <div className="page-header-inner">
              <div className="header-left">
                <button className="header-button back-button" onClick={this.goBack}>
                  <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path></svg>
                </button>
                <h1 className="header-title">
                    {t('privacy-policy-screen:title')}
                  </h1>
              </div>
            </div>
          </div>

          {/* Content - Single HTML variable for easy management */}
          <div 
            className="privacy-policy-content"
            dangerouslySetInnerHTML={{ __html: privacyContent.htmlContent }}
          />
        </div>
      </div>
    );
  }
}

// Navigation wrapper component - matching reference project pattern
const PrivacyPolicyPageWithNavigation: React.FC<WithTranslation> = (props) => {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <PrivacyPolicyPage
      {...props}
      navigate={handleNavigate}
    />
  );
};

// Redux connection matching reference project
const mapStateToProps = (state: any) => {
  return {};
};

const mapDispatchToProps = (dispatch: any) => {
  return {};
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(PrivacyPolicyPageWithNavigation));
